/**
 * Game-tab picker preview.
 *
 * Renders the REAL game scaled down to fit a square preview box, so the Brand
 * Studio and the Design Journey's game picker show each game exactly as the
 * consumer will see it — branded with the brand's `primary`/`accent`, in its
 * idle (survey-start) state.
 *
 * The art is a COPY of what Xurvey serves, not a shared package: Xotski shows
 * the live survey by URL, so this only has to look right, not run.
 *
 * The preview is non-interactive (`pointer-events: none`) so clicking the card
 * selects the game rather than playing it. The real game logic lives in
 * `features/public-survey/games/` and is reached through the shared registry.
 */
import { resolveGame } from '@/features/public-survey/games/registry';
import type { WheelItem } from '@/features/public-survey/games/pickPrize';

/**
 * Sample prizes used only to populate the preview boards (ratings span 1–5 so
 * every game's slots/segments/faces fill out). Local on purpose — the
 * Storybook fixture in `games/_sampleItems` is dev-only and tree-shaken.
 */
// `logo` uses the bundled Xotski mark (served same-origin by the frontend, so
// no upload host / CORS dependency) so the preview shows how a real prize logo
// sits on each game surface — the picker is about layout, not the actual art.
const PREVIEW_ITEMS: WheelItem[] = [
  { id: 1, name: 'Soda', rating: 1, available: 999, logo: '/xk-icon.svg' },
  { id: 2, name: 'Cap', rating: 2, available: 200, logo: '/xk-icon.svg' },
  { id: 3, name: 'Tote', rating: 3, available: 100, logo: '/xk-icon.svg' },
  { id: 4, name: 'Mug', rating: 4, available: 40, logo: '/xk-icon.svg' },
  { id: 5, name: 'VIP Pass', rating: 5, available: 6, logo: '/xk-icon.svg' },
];

const NOOP = () => {};

/** The width every game is laid out at before being scaled into the box. */
const NATURAL_WIDTH = 360;

export function GamePreview({
  code,
  size,
  primary,
  accent,
}: {
  code: string;
  size: number;
  primary: string;
  accent: string;
}) {
  const Game = resolveGame({ gameCode: code });
  if (!Game) return null;

  const scale = size / NATURAL_WIDTH;

  return (
    <div
      aria-hidden
      // `inert` removes the game's own buttons (sound toggle, action button)
      // from focus + the a11y tree so they don't trap Tab inside the picker
      // card. The card itself owns selection.
      inert
      style={{
        width: size,
        height: size,
        overflow: 'hidden',
        borderRadius: 12,
        position: 'relative',
        // Non-interactive: the card owns the click (select), not the game.
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: NATURAL_WIDTH,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        <Game items={PREVIEW_ITEMS} primary={primary} accent={accent} lang="en" onWin={NOOP} />
      </div>
    </div>
  );
}
